import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Line,
} from "recharts";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Calculator } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";

/**
 * @typedef {Object} ProjectionPoint
 * @property {number} year
 * @property {string} label
 * @property {number} deposits
 * @property {number} expected
 * @property {number} best
 * @property {number} worst
 */

const YEARS = 25;

// tweak these to match your product assumptions
const EXPECTED_ANNUAL_RETURN = 0.12; // 12%
const BEST_ANNUAL_RETURN = 0.16; // 16%
const WORST_ANNUAL_RETURN = 0.08; // 8%

function formatUGX(value) {
  if (!isFinite(value)) return "-";
  // Simple formatter e.g. 1.2B / 540M / 300K
  const abs = Math.abs(value);
  if (abs >= 1000000000) return `UGX ${Math.round(value / 1000000000)}B`;
  if (abs >= 1000000) return `UGX ${Math.round(value / 1000000)}M`;
  if (abs >= 1000) return `UGX ${Math.round(value / 1000)}K`;
  return `UGX ${Math.round(value).toLocaleString()}`;
}

const LoanCalculator = ({ propertyPrice: propPropertyPrice, isModal = false, onClose = null }) => {
  const { formatPrice } = useCurrency();
  const [propertyPrice, setPropertyPrice] = useState(propPropertyPrice || 50000000); // Default 50M UGX
  const [firstDeposit, setFirstDeposit] = useState(Math.min((propPropertyPrice || 50000000) * 0.2, 5000000)); // 20% or 5M max
  const [monthlyDeposit, setMonthlyDeposit] = useState(Math.min((propPropertyPrice || 50000000) * 0.05, 1000000)); // 5% or 1M max
  const [simulateKey, setSimulateKey] = useState(0); // just to re-run animation when clicking
  const [showCalculator, setShowCalculator] = useState(!isModal); // Show calculator immediately if modal

  // Update first deposit when property price changes
  React.useEffect(() => {
    if (propertyPrice) {
      const newFirstDeposit = Math.min(propertyPrice * 0.2, 20000000);
      const newMonthlyDeposit = Math.min(propertyPrice * 0.2, 5000000);
      setFirstDeposit(newFirstDeposit);
      setMonthlyDeposit(newMonthlyDeposit);
    }
  }, [propertyPrice]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const data = useMemo(() => {
    const monthlyExpected = EXPECTED_ANNUAL_RETURN / 12;
    const monthlyBest = BEST_ANNUAL_RETURN / 12;
    const monthlyWorst = WORST_ANNUAL_RETURN / 12;

    const rows = [];

    for (let year = 1; year <= YEARS; year++) {
      const months = year * 12;

      const deposits = firstDeposit + monthlyDeposit * months;

      const fv = (r) => {
        if (r === 0) return deposits;
        const pow = Math.pow(1 + r, months);
        const fvLump = firstDeposit * pow;
        const fvAnnuity = monthlyDeposit * ((pow - 1) / r);
        return fvLump + fvAnnuity;
      };

      rows.push({
        year,
        label: (new Date().getFullYear() + year).toString(),
        deposits,
        expected: fv(monthlyExpected),
        best: fv(monthlyBest),
        worst: fv(monthlyWorst),
      });
    }

    return rows;
  }, [firstDeposit, monthlyDeposit, simulateKey]);

  const last = data[data.length - 1];

  const totalDeposits = last?.deposits ?? 0;
  const mostLikely = last?.expected ?? 0;
  const bestCase = last?.best ?? 0;
  const worstCase = last?.worst ?? 0;

  // Calculate max slider values based on property price
  const maxFirstDeposit = Math.min(propertyPrice * 0.5, 20000000); // 50% or 20M max
  const maxMonthlyDeposit = Math.min(propertyPrice * 0.2, 5000000); // 20% or 5M max

  // Ensure first deposit doesn't exceed new max
  React.useEffect(() => {
    if (firstDeposit > maxFirstDeposit) {
      setFirstDeposit(maxFirstDeposit);
    }
    if (monthlyDeposit > maxMonthlyDeposit) {
      setMonthlyDeposit(maxMonthlyDeposit);
    }
  }, [maxFirstDeposit, maxMonthlyDeposit]);

  // For non-modal usage, show the button first
  if (!isModal && !showCalculator) {
    return (
      <Card className="border border-stone-200 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2">
                NyiNimu Calculator
              </h3>
              <p className="text-stone-600">
                Enter property price and calculate months to reach deposit goals
              </p>
            </div>
            <Button 
              onClick={() => setShowCalculator(true)}
              className="bg-[#81be00] text-black hover:bg-[#6a9e00] font-semibold"
            >
              <Calculator className="w-4 h-4 mr-2" />
              NyiNimu Calculator
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const calculatorContent = (
    <Card className={`border border-stone-200 shadow-lg bg-black text-white overflow-hidden ${isModal ? 'h-full' : ''}`}>
      <CardContent className={`p-0 ${isModal ? 'h-full' : ''}`}>
        {/* TOP ROW */}
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* LEFT PANEL */}
          <div className="bg-[#81be00] p-6 space-y-6">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-black uppercase">
                Property Price
              </p>
              <input
                type="number"
                min="1000000"
                step="100000"
                value={propertyPrice}
                onChange={(e) => setPropertyPrice(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-white text-black font-bold text-xl rounded-md border-2 border-black/20 focus:border-black focus:outline-none"
                placeholder="Enter property price"
              />
              <p className="text-xs text-black/70">
                Total price of the property you want to buy
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-black uppercase">
                First Deposit
              </p>
              <input
                type="range"
                min={0}
                max={Math.min(propertyPrice * 0.5, 20000000)}
                step={100000}
                value={firstDeposit}
                onChange={(e) => setFirstDeposit(parseInt(e.target.value))}
                className="w-full accent-black"
              />
              <p className="text-2xl font-bold text-black">
                {formatPrice(firstDeposit)}
              </p>
              <p className="text-xs text-black/70">
                {Math.round((firstDeposit / propertyPrice) * 100)}% of property price
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-black uppercase">
                Monthly Deposit
              </p>
              <input
                type="range"
                min={0}
                max={Math.min(propertyPrice * 0.2, 5000000)}
                step={50000}
                value={monthlyDeposit}
                onChange={(e) => setMonthlyDeposit(parseInt(e.target.value))}
                className="w-full accent-black"
              />
              <p className="text-2xl font-bold text-black">
                {formatPrice(monthlyDeposit)}
              </p>
              <p className="text-xs text-black/70">Monthly Deposite amount</p>
            </div>

            <button
              onClick={() => setSimulateKey((k) => k + 1)}
              className="px-6 py-2 bg-black text-white font-semibold rounded-full tracking-wide hover:bg-stone-800"
            >
              SIMULATE
            </button>

            <Button 
              onClick={handleClose}
              variant="outline"
              className="w-full border-black text-black hover:bg-black hover:text-white"
            >
              Close Calculator
            </Button>
          </div>

          {/* RIGHT PANEL: CHART */}
          <div className="bg-black p-6">
            <h3 className="text-teal-400 font-semibold mb-5 uppercase">
              Performance
            </h3>
            <div className="w-full h-64 bg-transparent">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="colorBand" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#81be00" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#81be00" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="label" stroke="#888" />
                  <YAxis
                    stroke="#888"
                    tickFormatter={(v) => {
                      if (v >= 1000000000) return `${Math.round(v / 1000000000)}B`;
                      if (v >= 1000000) return `${Math.round(v / 1000000)}M`;
                      if (v >= 1000) return `${Math.round(v / 1000)}K`;
                      return v.toString();
                    }}
                  />
                  <Tooltip
                    formatter={(value) => formatPrice(parseInt(value))}
                    labelFormatter={(label) => `Year ${label}`}
                    contentStyle={{ backgroundColor: "#111", borderColor: "#333" }}
                  />
                  {/* Band between worst and best */}
                  <Area
                    type="monotone"
                    dataKey="best"
                    stroke="none"
                    fillOpacity={1}
                    fill="url(#colorBand)"
                  />
                  <Area
                    type="monotone"
                    dataKey="worst"
                    stroke="none"
                    fill="#000"
                    fillOpacity={0.7}
                  />
                  {/* Deposits line */}
                  <Line
                    type="monotone"
                    dataKey="deposits"
                    stroke="#D946EF" // purple
                    strokeWidth={2}
                    dot={false}
                    name="Deposits"
                  />
                  {/* Expected line */}
                  <Line
                    type="monotone"
                    dataKey="expected"
                    stroke="#FACC15" // yellow
                    strokeWidth={2}
                    dot={false}
                    name="Most likely value"
                  />
                  <Legend />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-gray-400 mt-2 max-w-xl">
              This simulation shows a possible long-term outlook based on today's assumptions. Real values can vary. NyiNimu shares projections to guide your planning, not promise exact future returns.
            </p>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className="p-6 bg-[#0F1624]">
          {/* DAYS CALCULATION BOX */}
          <div className="space-y-4">
            <h3 className="text-teal-400 font-semibold uppercase text-sm text-center mb-6">Months to Reach Goals</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-gray-400 text-xs uppercase mb-2">Property Price</p>
                <p className="text-2xl font-bold text-white">
                  {formatPrice(propertyPrice)}
                </p>
              </div>

              <div className="text-center">
                <p className="text-gray-400 text-xs uppercase mb-2">First Deposit</p>
                <p className="text-2xl font-bold text-[#81be00]">
                  {monthlyDeposit > 0 
                    ? Math.ceil(firstDeposit / monthlyDeposit) 
                    : firstDeposit > 0 
                      ? "0 months (lump sum)" 
                      : "Set amount"
                  } months
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {Math.round((firstDeposit / propertyPrice) * 100)}% of property price
                </p>
              </div>

              <div className="text-center">
                <p className="text-gray-400 text-xs uppercase mb-2">25% Deposit</p>
                <p className="text-2xl font-bold">
                  {(() => {
                  const target = propertyPrice * 0.25;
                  const remaining = target - firstDeposit;
                    if (remaining <= 0) return "Reached";
                    if (monthlyDeposit <= 0) return "Set monthly";
                    const months = Math.ceil(remaining / monthlyDeposit);
                    return `${months} months`;
                  })()}
                </p>
                <p className="text-xs text-gray-400 mt-1">25% of property price</p>
              </div>

              <div className="text-center">
                <p className="text-gray-400 text-xs uppercase mb-2">50% Deposit</p>
                <p className="text-2xl font-bold">
                  {(() => {
                    const target = propertyPrice * 0.5;
                    const remaining = target - firstDeposit;
                    if (remaining <= 0) return "Reached";
                    if (monthlyDeposit <= 0) return "Set monthly";
                    const months = Math.ceil(remaining / monthlyDeposit);
                    return `${months} months`;
                  })()}
                </p>
                <p className="text-xs text-gray-400 mt-1">50% of property price</p>
              </div>
            </div>

            <div className="flex justify-center pt-6">
              <Button className="px-8 py-3 bg-[#81be00] text-black font-bold rounded-xl hover:bg-[#6a9e00]">
                GET STARTED
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
        <div className="w-full max-w-7xl h-full max-h-screen overflow-auto">
          {calculatorContent}
        </div>
      </div>
    );
  }

  return calculatorContent;
};

export default LoanCalculator;
